/**
 * Helper function to identify important tracking parameters that should be preserved
 * and included in analytics events. This centralizes the logic to avoid duplication
 * between client and server code.
 *
 * @param key - The parameter key to check
 * @returns Whether this parameter should be tracked
 */
export function isImportantTrackingParam(key: string): boolean {
  if (key.startsWith('utm_')) return true;
  if (['gclid', 'gbraid', 'wbraid', 'fbclid', 'msclkid', 'ttclid'].includes(key)) return true;
  if (['ref', 'referrer', 'affiliate', 'partner', 'sub_first', 'annual'].includes(key)) return true;
  if (['campaign', 'promo', 'discount'].includes(key)) return true;

  return false;
}

/**
 * Get all important tracking parameters from a URL parameters object
 *
 * @param urlParams - Object containing URL parameters
 * @returns Filtered object containing only important tracking parameters
 */
export function getImportantTrackingParams(urlParams: Record<string, string> | null | undefined): Record<string, string> {
  if (!urlParams || typeof urlParams !== 'object' || Array.isArray(urlParams)) return {};

  const importantParams: Record<string, string> = {};
  Object.keys(urlParams).forEach(key => {
    const value = urlParams[key];
    if (isImportantTrackingParam(key) && typeof value === "string" && !containsAuthenticationParams(value)) {
      importantParams[key] = value;
    }
  });

  return importantParams;
}

// Referrers/campaign values sometimes contain encoded continuation URLs. Drop
// credential-bearing values rather than attempting to retain parts of a login URL.
function containsAuthenticationParams(value: string): boolean {
  let decoded = value;
  for (let i = 0; i < 8; i++) {
    if (/(?:[?&#]|^)(?:oobCode|linkKey|access_token|id_token|refresh_token|token|code|password|email|continueUrl)=/i.test(decoded)) return true;
    let next: string;
    try {
      next = decodeURIComponent(decoded.replace(/%(?![0-9a-f]{2})/gi, "%25"));
    } catch {
      return true;
    }
    if (next === decoded) return false;
    decoded = next;
  }
  return true;
}

export function isAuthenticationPath(pathname: string): boolean {
  return /^\/(?:email-sign-in(?:-verify)?|email-verify|authenticate_mobile(?:\/.*)?|__\/auth(?:\/.*)?)\/?$/i.test(pathname);
}

// Attribution needs the origin/path and campaign tags, not arbitrary URL state.
export function sanitizeAttributionUrl(value: string | null | undefined): string | null {
  if (!value || typeof value !== "string") return null;
  try {
    const url = new URL(value);
    if (url.protocol !== "https:" && url.protocol !== "http:") return null;
    const safeParams = isAuthenticationPath(url.pathname) ? {} : getImportantTrackingParams(Object.fromEntries(url.searchParams));
    url.search = new URLSearchParams(safeParams).toString();
    url.hash = "";
    url.username = "";
    url.password = "";
    return url.toString();
  } catch {
    return null;
  }
}
